import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Stack,
  IconButton,
  Typography,
  Divider,
  Paper,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import {
  DiagnosisIndicatorsReadDTO,
  DiagnosisIndicatorsCreateDTO,
  DiagnosisMetersReadDTO,
  DiagnosisRelevantVariablesReadDTO,
  DiagnosisConsumptionReadDTO,
} from '../../../generated/api-client';
import { apiClient, axiosInstance } from '../../../services/apiClient';
import { ActionButton } from '../../common';
import { DropdownEntity } from '../../common/DropdownEntity';
import { EnergyUseDropdown } from '../../common/EnergyUseDropdown';
import {
  useDiagnosisMetersByDiagnosisId,
  useDiagnosisRelevantVariablesByDiagnosisId,
  useDiagnosisConsumptionsByDiagnosisId,
} from '../../../hooks';

interface MeterWithCode {
  meter_id: string;
  code: string;
}

interface VariableWithCode {
  variable_id: string;
  code: string;
}

interface ConsumptionWithCode {
  consumption_id: string;
  code: string;
}

interface EditIndicatorDialogProps {
  open: boolean;
  onClose: () => void;
  indicator: DiagnosisIndicatorsReadDTO | null;
  diagnosisId: string;
  existingIndicators?: DiagnosisIndicatorsReadDTO[]; // Per calcolare l'index
}

export const EditIndicatorDialog: React.FC<EditIndicatorDialogProps> = ({
  open,
  onClose,
  indicator,
  diagnosisId,
  existingIndicators = [],
}) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<
    DiagnosisIndicatorsCreateDTO & {
      index?: number;
      udm_desiderata?: string;
      meters?: MeterWithCode[];
      variables?: VariableWithCode[];
      consumptions?: ConsumptionWithCode[];
    }
  >();

  const {
    fields: meterFields,
    append: appendMeter,
    remove: removeMeter,
  } = useFieldArray({
    control,
    name: 'meters',
  });

  const {
    fields: variableFields,
    append: appendVariable,
    remove: removeVariable,
  } = useFieldArray({
    control,
    name: 'variables',
  });

  const {
    fields: consumptionFields,
    append: appendConsumption,
    remove: removeConsumption,
  } = useFieldArray({
    control,
    name: 'consumptions',
  });

  // Carica contatori e variabili associati all'indicatore se esiste
  const { data: indicatorMeters = [] } = useQuery<any[]>(
    ['indicator-meters', indicator?.id],
    () => {
      if (!indicator?.id) return Promise.resolve([]);
      return apiClient.get_by_indicator_id_diagnosis_indicator_meters_by_indicator__indicator_id__get(
        indicator.id
      );
    },
    { enabled: !!indicator?.id }
  );

  const { data: indicatorVariables = [] } = useQuery<any[]>(
    ['indicator-variables', indicator?.id],
    () => {
      if (!indicator?.id) return Promise.resolve([]);
      return apiClient.get_by_indicator_id_diagnosis_indicator_relevant_variables_by_indicator__indicator_id__get(
        indicator.id
      );
    },
    { enabled: !!indicator?.id }
  );

  const { data: indicatorConsumptions = [] } = useQuery<any[]>(
    ['indicator-consumptions', indicator?.id],
    async () => {
      if (!indicator?.id) return [];
      const res = await axiosInstance.get(
        `/diagnosis-indicator-consumptions/by-indicator/${indicator.id}`
      );
      return res.data;
    },
    { enabled: !!indicator?.id }
  );

  // Carica contatori e variabili disponibili per ottenere le unità di misura
  const { data: availableMeters = [] } = useDiagnosisMetersByDiagnosisId(diagnosisId!);
  const { data: availableVariables = [] } = useDiagnosisRelevantVariablesByDiagnosisId(
    diagnosisId!
  );

  // Carica consumi disponibili per la diagnosi (una sola API)
  const { data: availableConsumptions = [] } = useDiagnosisConsumptionsByDiagnosisId(diagnosisId!);

  // Calcola l'index per un nuovo indicatore
  const calculateNewIndex = React.useCallback((): number => {
    if (indicator) {
      return (indicator as any).index ?? existingIndicators.length + 1;
    }
    return existingIndicators.length + 1;
  }, [indicator, existingIndicators]);

  React.useEffect(() => {
    if (!open) return; // il dialog non è aperto
    if (!indicator) return; // niente indicatore
    if (!indicatorMeters || !indicatorVariables || indicatorConsumptions === undefined) return; // stiamo ancora caricando

    // Crea il payload di reset quando TUTTO è pronto
    reset({
      diagnosis_id: diagnosisId,
      code: indicator.code ?? '',
      index: (indicator as any).index ?? existingIndicators.length + 1,
      formula: indicator.formula ?? undefined,
      udm_desiderata: (indicator as any).udm_desiderata ?? undefined,
      energy_use_id: (indicator as any).energy_use_id ?? undefined,
      correlation_r2: indicator.correlation_r2 ?? undefined,
      reference_ipe: indicator.reference_ipe ?? undefined,
      reference_ipe_unit: indicator.reference_ipe_unit ?? undefined,
      min_relevant_variable_field: indicator.min_relevant_variable_field ?? undefined,
      max_relevant_variable_field: indicator.max_relevant_variable_field ?? undefined,
      reliability_level: indicator.reliability_level ?? undefined,
      ipe_source: indicator.ipe_source ?? undefined,
      report_table_graph: indicator.report_table_graph ?? undefined,
      notes: indicator.notes ?? undefined,

      meters: indicatorMeters.map(im => ({
        meter_id: im.meter_id,
        code: im.code || '',
      })),

      variables: indicatorVariables.map((iv: any) => ({
        variable_id: iv.relevant_variable_id,
        code: iv.code || '',
      })),

      consumptions: indicatorConsumptions.map((ic: any) => ({
        consumption_id: ic.consumption_id,
        code: ic.code || '',
      })),
    });
  }, [
    open,
    indicator,
    indicatorMeters,
    indicatorVariables,
    indicatorConsumptions,
    reset,
    diagnosisId,
    existingIndicators,
  ]);

  const createMutation = useMutation(
    async (data: any) => {
      // Usa la nuova route che gestisce meters e variables
      return apiClient.create_with_relations_diagnosis_indicators_create_with_relations_post(data);
    },
    {
      onSuccess: async result => {
        queryClient.invalidateQueries(`diagnosis-indicators-${diagnosisId}`);
        // Ricarica immediatamente i dati calcolati dell'indicatore appena creato
        await queryClient.refetchQueries(['all-indicator-values-calculated', diagnosisId]);
        toast.success('Indicatore creato con successo');
        handleClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nella creazione dell'indicatore");
      },
    }
  );

  const updateMutation = useMutation(
    async (data: any) => {
      if (!indicator?.id) {
        throw new Error('ID indicatore mancante');
      }
      // Usa la nuova route che gestisce meters e variables
      return apiClient.update_with_relations_diagnosis_indicators_update_with_relations__item_id__put(
        indicator.id,
        data
      );
    },
    {
      onSuccess: async result => {
        queryClient.invalidateQueries(`diagnosis-indicators-${diagnosisId}`);
        // Ricarica immediatamente i dati calcolati dell'indicatore aggiornato
        await queryClient.refetchQueries(['all-indicator-values-calculated', diagnosisId]);
        toast.success('Indicatore aggiornato con successo');
        handleClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nell'aggiornamento dell'indicatore");
      },
    }
  );

  const onSubmit = async (data: any) => {
    const submitData: any = {
      ...data,
      index: data.index ?? calculateNewIndex(),
    };
    // Assicurati che meters, variables e consumptions siano sempre presenti (anche se array vuoti)
    const meters = submitData.meters || [];
    const variables = submitData.variables || [];
    const consumptions = submitData.consumptions || [];
    const formula = submitData.formula;
    const udmDesiderata = submitData.udm_desiderata;

    // Se c'è una formula, testala prima di salvare
    if (formula) {
      // Costruisci il payload di test con tutti i valori a 3
      const testValori: { [key: string]: number } = {};
      const testVariabili: Array<{ nome: string; udm: string; densita: number }> = [];

      // Aggiungi meters al test
      meters.forEach((m: MeterWithCode) => {
        if (m.code && m.meter_id) {
          const meter = availableMeters.find(am => am.id === m.meter_id);
          const udm = (meter as any)?.unit_of_measurement || 'm';
          testValori[m.code] = 3;
          testVariabili.push({
            nome: m.code,
            udm: udm,
            densita: 1.0,
          });
        }
      });

      // Aggiungi variables al test
      variables.forEach((v: VariableWithCode) => {
        if (v.code && v.variable_id) {
          const variable = availableVariables.find(av => av.id === v.variable_id);
          const udm = variable?.unit_of_measurement || 'm';
          testValori[v.code] = 3;
          testVariabili.push({
            nome: v.code,
            udm: udm,
            densita: 1.0,
          });
        }
      });

      // Aggiungi consumptions al test
      consumptions.forEach((c: ConsumptionWithCode) => {
        if (c.code && c.consumption_id) {
          const consumption = availableConsumptions.find(ac => ac.id === c.consumption_id);
          const udm = (consumption as any)?.unit_of_measurement || 'm';
          testValori[c.code] = 3;
          testVariabili.push({
            nome: c.code,
            udm: udm,
            densita: 1.0,
          });
        }
      });

      // Chiama l'endpoint di test
      try {
        const testResult = await apiClient.test_formula_diagnosis_indicators_test_formula_post({
          formula,
          valori: testValori,
          variabili: testVariabili,
          udm_desiderata: udmDesiderata || undefined,
        });

        if (testResult.error) {
          toast.error(`Errore nella formula: ${testResult.error}`);
          return; // Blocca il salvataggio
        }
      } catch (error: any) {
        toast.error(`Errore nel test della formula: ${error.message || 'Errore sconosciuto'}`);
        return; // Blocca il salvataggio
      }

      // Se il test è ok, procedi con il salvataggio
    }

    // Procedi con il salvataggio
    const payload = {
      diagnosis_id: diagnosisId,
      ...submitData,
    };

    if (indicator) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      // Reset del form con valori di default che includono arrays vuoti per meters e variables
      reset(
        {
          diagnosis_id: diagnosisId,
          code: '',
          index: null as any,
          formula: '',
          udm_desiderata: '',
          energy_use_id: undefined,
          correlation_r2: '',
          reference_ipe: '',
          reference_ipe_unit: '',
          min_relevant_variable_field: null as any,
          max_relevant_variable_field: null as any,
          reliability_level: '',
          ipe_source: '',
          report_table_graph: '',
          notes: '',
          meters: [],
          variables: [],
          consumptions: [],
        },
        {
          keepDefaultValues: false,
          keepValues: false,
          keepErrors: false,
          keepDirty: false,
          keepIsSubmitted: false,
          keepTouched: false,
          keepIsValid: false,
          keepSubmitCount: false,
        }
      );
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{indicator ? 'Modifica indicatore' : 'Nuovo indicatore'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Codice" {...register('code')} error={!!errors.index} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Ordine"
                {...register('index', { valueAsNumber: true })}
                error={!!errors.index}
                helperText={
                  errors.index?.message ||
                  'Calcolato automaticamente in base al numero di indicatori esistenti'
                }
                type="number"
              />
            </Grid>

            {/* Sezione Contatori */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Contatori
              </Typography>
              {meterFields.map((field, index) => (
                <Box key={field.id} sx={{ my: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Controller
                        name={`meters.${index}.meter_id`}
                        control={control}
                        render={({ field: f, fieldState }) => (
                          <DropdownEntity<DiagnosisMetersReadDTO>
                            label="Contatore"
                            value={f.value ?? null}
                            queryKey={['diagnosis-meters-dropdown', diagnosisId]}
                            fetchEntities={() =>
                              apiClient.search_by_diagnosis_id_diagnosis_meters_search_by_diagnosis_id__diagnosis_id__get(
                                diagnosisId!
                              )
                            }
                            getOptionLabel={option =>
                              `${option.meter_code} ${option.meter_customer_code && `- ${option.meter_customer_code}`} [${option.unit_of_measurement}]` ||
                              ''
                            }
                            getOptionValue={option => option.id ?? ''}
                            onChange={value => f.onChange(value)}
                            enabled={!!diagnosisId}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            placeholder="Seleziona contatore"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <TextField
                        fullWidth
                        label="Codice (per formula)"
                        {...register(`meters.${index}.code`)}
                        placeholder="es. $A, $B, etc."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <IconButton onClick={() => removeMeter(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  const currentMeters = watch('meters') || [];
                  const codeIndex = currentMeters.length + 1;
                  appendMeter({ meter_id: '', code: `c${codeIndex}` });
                }}
                variant="contained"
                size="small"
              >
                Aggiungi contatore
              </Button>
            </Grid>

            {/* Sezione Variabili Pertinenti */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Variabili Pertinenti
              </Typography>
              {variableFields.map((field, index) => (
                <Box key={field.id} sx={{ my: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Controller
                        name={`variables.${index}.variable_id`}
                        control={control}
                        render={({ field: f, fieldState }) => (
                          <DropdownEntity<DiagnosisRelevantVariablesReadDTO>
                            label="Variabile pertinente"
                            value={f.value ?? null}
                            queryKey={['diagnosis-relevant-variables-dropdown', diagnosisId]}
                            fetchEntities={() =>
                              apiClient.search_by_diagnosis_id_diagnosis_relevant_variables_search_by_diagnosis_id__diagnosis_id__get(
                                diagnosisId!
                              )
                            }
                            getOptionLabel={option =>
                              `${option.table_code_index} [${option.unit_of_measurement}]` || ''
                            }
                            getOptionValue={option => option.id ?? ''}
                            onChange={value => f.onChange(value)}
                            enabled={!!diagnosisId}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            placeholder="Seleziona variabile"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <TextField
                        fullWidth
                        label="Codice (per formula)"
                        {...register(`variables.${index}.code`)}
                        placeholder="es. $A, $B, etc."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <IconButton onClick={() => removeVariable(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  const currentVariables = watch('variables') || [];
                  const codeIndex = currentVariables.length + 1;
                  appendVariable({ variable_id: '', code: `v${codeIndex}` });
                }}
                variant="contained"
                size="small"
              >
                Aggiungi variabile
              </Button>
            </Grid>

            {/* Sezione Consumi */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Consumi
              </Typography>
              {consumptionFields.map((field, index) => (
                <Box key={field.id} sx={{ my: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 5 }}>
                      <Controller
                        name={`consumptions.${index}.consumption_id`}
                        control={control}
                        render={({ field: f, fieldState }) => (
                          <DropdownEntity<DiagnosisConsumptionReadDTO>
                            label="Consumo"
                            value={f.value ?? null}
                            queryKey={['diagnosis-consumptions-dropdown', diagnosisId]}
                            fetchEntities={async () => {
                              if (!diagnosisId) return [];
                              const res = await axiosInstance.get<DiagnosisConsumptionReadDTO[]>(
                                `/diagnosis-consumption/by-diagnosis/${diagnosisId}`
                              );
                              return res.data;
                            }}
                            getOptionLabel={option =>
                              `${option.code} [${option.unit_of_measurement || ''}]` || ''
                            }
                            getOptionValue={option => option.id ?? ''}
                            onChange={value => f.onChange(value)}
                            enabled={!!diagnosisId}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            placeholder="Seleziona consumo"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <TextField
                        fullWidth
                        label="Codice (per formula)"
                        {...register(`consumptions.${index}.code`)}
                        placeholder="es. n1, n2, etc."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2 }}>
                      <IconButton onClick={() => removeConsumption(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  const currentConsumptions = watch('consumptions') || [];
                  const codeIndex = currentConsumptions.length + 1;
                  appendConsumption({ consumption_id: '', code: `n${codeIndex}` });
                }}
                variant="contained"
                size="small"
                sx={{ mb: 2 }}
              >
                Aggiungi consumo
              </Button>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Formula"
                {...register('formula')}
                error={!!errors.formula}
                helperText={
                  errors.formula?.message ||
                  'Usa i codici contatori (c1…), variabili (v1…) e consumi (n1…) (es. n1/v1 o c1*n1)'
                }
                multiline
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Unità di misura desiderata"
                {...register('udm_desiderata')}
                error={!!errors.udm_desiderata}
                helperText={errors.udm_desiderata?.message || 'es. m/(kg*l) o kWh/m^2'}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="energy_use_id"
                control={control}
                render={({ field, fieldState }) => (
                  <EnergyUseDropdown
                    diagnosisId={diagnosisId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Correlazione R2"
                {...register('correlation_r2')}
                error={!!errors.correlation_r2}
                helperText={errors.correlation_r2?.message}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Valore baseline"
                {...register('reference_ipe')}
                error={!!errors.reference_ipe}
                helperText={errors.reference_ipe?.message}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Grafico"
                {...register('report_table_graph')}
                error={!!errors.report_table_graph}
                helperText={errors.report_table_graph?.message}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Note"
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip="Salva"
              onClick={handleSubmit(onSubmit)}
              variant="filled"
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};
